import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetTicketNumberSequenceQuery as GetTicketNumberSequence } from '__generated__/graphql';
import { encodeUrl, getServerErrors } from '../../../common/helpers';
import RegistrationFilters, {
  RegistrationFilterParams, mapRegistrationFiltersToServerInput, mapServerOutputToRegistrationFilters,
} from '../Registrations/RegistrationFilters';
import UI from '../../../common/UI';
import useEditTicketNumberSequenceMutation from './useEditTicketNumberSequenceMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

type TicketNumberSequence = GetTicketNumberSequence['project']['ticket_number_sequence'];

const editTicketNumberSequenceCriteriaSchema = yup.object().shape({});

interface EditTicketNumberSequenceCriteriaFormProps {
  sequence: TicketNumberSequence;
}

const EditTicketNumberSequenceCriteriaForm = ({
  sequence,
}: EditTicketNumberSequenceCriteriaFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (sequence: TicketNumberSequence) => ({
    filters: mapServerOutputToRegistrationFilters(sequence.filters),
  });

  const form = useForm({
    values: getValues(sequence),
  });

  useNavigationPrompt(form.changed());

  const [editTicketNumberSequence, { error, loading }] = useEditTicketNumberSequenceMutation({
    input: {
      id: sequence.id,
      filters: mapRegistrationFiltersToServerInput(form.values.filters),
    },
    onSuccess: (sequence) => {
      form.reset(getValues(sequence));
    },
  });

  const { valid } = useValidation({
    schema: editTicketNumberSequenceCriteriaSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const handleFiltersChange = (newFilters: RegistrationFilterParams) => {
    form.set((values) => ({
      ...values,
      filters: newFilters,
    }));
  };

  return (
    <UI.Form onSubmit={() => editTicketNumberSequence()}>
      <UI.FormGrid>
        {sequence.assigned_ticket_number_count > 0 && (
          <UI.Info>
            {t('edit_sequence_ticket_numbers_assigned_description')}
          </UI.Info>
        )}
        <UI.ServerErrors error={error} />
        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('criteria')}</UI.Legend>
          <UI.InputDescription>
            {t('ticket_number_sequence_criteria_description')}
          </UI.InputDescription>
        </UI.Div>
        <RegistrationFilters
          filters={form.values.filters}
          onChange={handleFiltersChange}
          types={['age', 'gender', 'tickets', 'products', 'participant_fields', 'invitations', 'coupons', 'team']}
          showResetButton={false}
          showViewRegistrationsButton
        />
        <UI.HR />
        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket_number_sequence')}
          closeTo={`/${project.id}/participants/bib-numbers?${encodeUrl({ event: sequence.event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTicketNumberSequenceCriteriaForm;
